import React from 'react';

/**
 * URL policy for every ReactMarkdown render site (TP3015 finding 6.3).
 *
 * Model-generated markdown is untrusted: without a `urlTransform`,
 * react-markdown v10's default transform lets any http(s) URL through, so a
 * summary- or model-authored image would auto-fetch an attacker URL on
 * render (an exfiltration channel), and links could carry look-alike
 * schemes. This module centralizes the allowlist and the non-fetching image
 * renderer that every markdown site shares.
 *
 * @typedef {Object} MarkdownImageProps
 * @property {string} [src] - Image source, already run through the
 *   `urlTransform` by react-markdown ('' when the URL was blocked).
 * @property {string} [alt] - Author-supplied alt text.
 */

/** Protocols allowed regardless of origin. */
const ALLOWED_PROTOCOLS = ['https:', 'mailto:', 'tel:'];

/**
 * Inert replacement per react-markdown v10 semantics: the transform's return
 * value is written to the attribute verbatim, and an empty string produces a
 * non-navigating href / non-fetching src.
 */
const INERT_URL = '';

/**
 * `urlTransform` for react-markdown v10: allow only `https:`, `mailto:`,
 * `tel:`, and same-origin/relative URLs. Everything else — `http:`
 * cross-origin, `javascript:`, `data:`, `vbscript:`, `file:`, `blob:`,
 * protocol-relative to a foreign host, unparsable input — becomes ''.
 *
 * Parsing uses the WHATWG `URL` resolved against the current page, so
 * scheme obfuscation (case tricks, embedded tabs/newlines) is normalized
 * before the check, and relative URLs are allowed exactly when they resolve
 * to the page's own origin.
 *
 * @param {string} url - Raw URL from the markdown AST (href, src, ...).
 * @returns {string} The URL unchanged when allowed, '' (inert) when blocked.
 */
export default function markdownUrlPolicy(url) {
  if (typeof url !== 'string' || url.trim() === '') {
    return INERT_URL;
  }

  let parsed;
  try {
    parsed = new URL(url, window.location.href);
  } catch (parseError) {
    return INERT_URL;
  }

  if (ALLOWED_PROTOCOLS.includes(parsed.protocol)) {
    return url;
  }

  // Same-origin/relative allowance is restricted to http(s): a blob: URL
  // inherits its inner URL's origin and must not ride through on it.
  if (
    (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
    parsed.origin === window.location.origin
  ) {
    return url;
  }

  return INERT_URL;
}

/**
 * `img` component override for react-markdown: model-authored images are
 * never auto-fetched. The image renders as its alt text plus the
 * (policy-transformed) URL as a normal safe link the user must click; when
 * the URL was blocked by {@link markdownUrlPolicy} only inert text remains.
 *
 * @param {MarkdownImageProps} props - Props from react-markdown.
 * @returns {React.ReactElement} A link or an inert placeholder — never <img>.
 */
export function SafeMarkdownImage({ src, alt }) {
  const href = typeof src === 'string' ? src : '';
  const label = alt && alt.trim() ? alt.trim() : 'image';

  if (!href) {
    return <span className="markdown-image-blocked">[image: {label}]</span>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="markdown-link markdown-image-link"
    >
      [image: {label}] ({href})
    </a>
  );
}
